import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  MonetizationOn as FundingIcon,
  Science as ScienceIcon,
  Biotech as LabIcon,
  BookmarkBorder as BookmarkIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  PersonAdd as FollowIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';
import { useTheme } from '@mui/material/styles';
import useSWR from 'swr';
import FollowButton from "components/common/buttons/FollowButton";
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';

const FollowedEntitiesPage = ({ embedded = false, compact = false, maxItems = null }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  // 'following' | 'followers' — which sub-section is active on the Users tab
  const [userSection, setUserSection] = useState(
    () => searchParams.get('section') === 'followers' ? 'followers' : 'following'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const [entities, setEntities] = useState({
    users: [],
    organizations: [],
    consortia: [],
    fundingAgencies: [],
    fundingPrograms: [],
    calls: [],
    labs: [],
  });
  const [entityCounts, setEntityCounts] = useState({
    users: 0,
    organizations: 0,
    consortia: 0,
    fundingAgencies: 0,
    fundingPrograms: 0,
    calls: 0,
    labs: 0,
  });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [followers, setFollowers] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const navigate = useNavigate();

  const pageSize = 50; // Adjust based on screen size

  const allEntityTypes = [
    { label: 'Connections', key: 'users', icon: <PersonIcon />, contentType: 'user' },
    { label: 'Organizations', key: 'organizations', icon: <BusinessIcon />, contentType: 'organization' },
    { label: 'Consortia', key: 'consortia', icon: <GroupIcon />, contentType: 'consortium' },
    { label: 'Funding Agencies', key: 'fundingAgencies', icon: <FundingIcon />, contentType: 'fundingagency' },
    { label: 'Funding Programs', key: 'fundingPrograms', icon: <ScienceIcon />, contentType: 'fundingprogram' },
    { label: 'Calls', key: 'calls', icon: <BookmarkIcon />, contentType: 'call' },
    { label: 'Labs', key: 'labs', icon: <LabIcon />, contentType: 'lab' },
  ];
  // the page: entities only (the people you follow are your connections: /connections/); the embedded panel keeps Users
  const entityTypes = embedded ? allEntityTypes : allEntityTypes.filter((t) => t.key !== 'users');

  const currentEntityType = entityTypes[activeTab];

  // Use SWR to fetch entity counts with caching (replaces fetchAllEntityCounts)
  const { data: countsData } = useSWR(
    '/interact/follows/following/?count_only=true',
    async () => {
      const res = await axiosService.get('/interact/follows/following/', {
        params: { page: 1, page_size: 100 }, // Reduced from 1000 to 100
      });
      const follows = res.data?.results || res.data || [];
      
      // Count only non-orphaned follows (followed_object is null when the target was deleted)
      const itemCounts = follows.reduce((acc, item) => {
        if (item.followed_object !== null) {
          const contentType = item.content_type;
          acc[contentType] = (acc[contentType] || 0) + 1;
        }
        return acc;
      }, {});
      
      // Map content_type to entity keys
      return entityTypes.reduce((acc, type) => {
        acc[type.key] = itemCounts[type.contentType] || 0;
        return acc;
      }, {});
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 120000, // Cache for 2 minutes
    }
  );

  // Use SWR to fetch followed entities with caching (replaces fetchFollowedEntities)
  const { data: followsData, error: followsError } = useSWR(
    ['/interact/follows/following/', currentEntityType.contentType, page, pageSize, maxItems],
    async ([endpoint, contentType, page, pageSize, maxItems]) => {
      const res = await axiosService.get(endpoint, {
        params: {
          page,
          page_size: maxItems || pageSize,
        },
      });
      const allFollows = res.data?.results || res.data || [];
      // Filter by content_type client-side (the following action doesn't support filtering)
      return allFollows.filter(follow => follow.content_type === contentType);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // Cache for 1 minute
    }
  );

  // Update entity counts when data is fetched
  useEffect(() => {
    if (countsData) {
      setEntityCounts(countsData);
    }
  }, [countsData]);

  // Update entities when follows data changes
  useEffect(() => {
    if (followsData) {
      // Use followed_object data already embedded in each follow record.
      // Filters out orphaned follows (where the followed object was deleted).
      const simplified = followsData
        .filter(follow => follow.followed_object !== null)
        .map(follow => ({
          ...follow.followed_object,
          id: follow.object_id,
          followId: follow.id,
          followedAt: follow.created,
          name: follow.followed_object?.name || follow.followed_object?.title,
          content_type: follow.content_type,
        }));

      setEntities(prev => ({ ...prev, [currentEntityType.key]: simplified }));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followsData, currentEntityType.key]);

  useEffect(() => {
    setError(followsError ? 'Failed to load followed entities' : null);
  }, [followsError]);

  // Fetch followers (users who follow me) when on the Users tab
  useEffect(() => {
    if (activeTab !== 0 || !embedded) return;
    setFollowersLoading(true);
    axiosService.get('/interact/follows/followers/', { params: { page_size: 100 } })
      .then((res) => {
        const raw = res.data?.results ?? res.data ?? [];
        setFollowers(raw.map(f => {
          const u = f.user_info || {};
          return {
            ...u,
            id: u.id ?? f.user,
            followId: f.id,
            followedAt: f.created,
            name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
            content_type: 'user',
          };
        }).filter(f => f.id));
      })
      .catch(() => setFollowers([]))
      .finally(() => setFollowersLoading(false));
  }, [activeTab, embedded]);

  // Filter entities based on search term
  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) {
      return entities[currentEntityType.key];
    }

    const searchLower = searchTerm.toLowerCase();
    return entities[currentEntityType.key].filter((entity) => {
      const name = entity.name || entity.username || entity.title || '';
      const email = entity.email || '';
      const orgType = entity.org_type || '';
      
      return (
        name.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower) ||
        orgType.toLowerCase().includes(searchLower)
      );
    });
  }, [entities, currentEntityType.key, searchTerm]);

  const handleUnfollow = async (followId, entityKey, entityId) => {
    try {
      await axiosService.delete(`/interact/follows/${followId}/`);
      
      // Remove from local state
      setEntities((prev) => ({
        ...prev,
        [entityKey]: prev[entityKey].filter((e) => e.id !== entityId),
      }));
      
      // Note: SWR will automatically revalidate on next access
      // No need to manually refresh counts
    } catch (err) {
      // console.error('Error unfollowing entity:', err);
      setError('Failed to unfollow. Please try again.');
    }
  };

  const handleViewEntity = (entity, type) => {
    if (type === 'users') {
      navigate(`/profile/${entity.id}/`);
    } else if (type === 'organizations') {
      navigate(`/organization/${entity.id}/`);
    } else if (type === 'consortia') {
      navigate(`/consortium/${entity.id}/`);
    } else if (type === 'fundingAgencies') {
      navigate(`/funding/agencies/${entity.id}/`);
    } else if (type === 'fundingPrograms') {
      navigate(`/funding/programs/${entity.id}/`);
    } else if (type === 'calls') {
      navigate(`/funding/call/${entity.id}/`);
    } else if (type === 'labs') {
      navigate(`/lab/${entity.id}/`);
    }
  };

  const renderEntityCard = (entity, type) => {
    const getEntityName = () => {
      if (type === 'users') return entity.name || entity.username;
      return entity.name || entity.title ;
    };

    const getEntitySubtitle = () => {
      if (type === 'users') return entity.position;
      if (type === 'organizations') return entity.org_type
        ? entity.org_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : '';
      if (type === 'consortia') return `${entity.members_count || 0} members`;
      if (type === 'calls') return entity.program?.name || entity.funding_agency?.name || '';
      if (type === 'labs') return [entity.organization_name, entity.pi_name && `PI ${entity.pi_name}`].filter(Boolean).join(' · ');
      return '';
    };

    const getEntityAvatar = () => {
      if (type === 'users') return entity.avatar;
      if (type === 'organizations') return null; // Organizations don't have avatars
      if (type === 'labs') return entity.avatar;
      return null;
    };

    // Compact mode: just avatar and name as link
    if (compact) {
      return (
        <Box 
          key={entity.id}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            py: 0.75,
            px: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
          onClick={() => handleViewEntity(entity, type)}
        >
          <Avatar
            src={getEntityAvatar()}
            sx={{ width: 32, height: 32, 

              backgroundColor: theme.palette.primary.avatar,
              color: 'white',

             }}
          >
            {currentEntityType.icon}
          </Avatar>
          <Typography 
            variant="body2" 
            sx={{ 
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': { color: 'primary.main' }
            }}
          >
            {/* {console.log('Rendering entity in compact mode:', entity)} */}
            {getEntityName()}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid item xs={12} sm={6} md={embedded ? 12 : 4} lg={embedded ? 6 : 3} xl={embedded ? 4 : 2} key={entity.id}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
   
        <CardContent sx={{ flexGrow: 1, p: embedded ? 1.5 : 2 }} 
          onClick={() => handleViewEntity(entity, type)}
          onMouseEnter={(e) => e.currentTarget.style.cursor = 'pointer'}
        >
  <Stack spacing={embedded ? 1 : 2}>
    {/* Top row: Avatar on left, info on right */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Avatar
        src={getEntityAvatar()}
        sx={{ 
          width: embedded ? 48 : 64, 
          height: embedded ? 48 : 64, 
          backgroundColor: theme.palette.primary.avatar,
          color: 'white',
          flexShrink: 0,
          fontSize: '3rem',
          fontWeight: 600,
        }}
      >
        {currentEntityType.icon}
      </Avatar>
      
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant={embedded ? "subtitle2" : "h6"} noWrap>
          {getEntityName()}
        </Typography>
        <Typography variant={embedded ? "caption" : "body2"} color="text.secondary" noWrap>
          {getEntitySubtitle()}
        </Typography>
        
        {entity.followedAt && (
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Since {new Date(entity.followedAt).toLocaleDateString()}
          </Typography>
          // <Chip
          //   label={`Since ${new Date(entity.followedAt).toLocaleDateString()}`}
          //   size="small"
          //   variant="outlined"
          //   sx={{ mt: 0.5 }}
          // />
        )}
      </Box>
    </Box>

    {/* Bottom center: Follow button */}
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <FollowButton
        contentType={currentEntityType.contentType}
        objectId={entity.id}
        size="medium"
        variant="outlined"
        sx={{ fontSize: '1rem' }}
      />
    </Box>
  </Stack>
</CardContent>



        </Card>
      </Grid>
    );
  };

  const ContainerComponent = embedded ? Box : PageContainer;
  const containerProps = embedded 
    ? compact 
      ? {} // No extra styling for compact mode, parent handles it
      : { sx: { bgcolor: 'background.paper', borderRadius: 2, p: 2, boxShadow: 2, height: '100%' } }
    : {};

  return (
    <ContainerComponent {...containerProps}>
      {/* {!embedded && } */}
      
      {/* the page: the tabs next to the title, as on the other pages */}
      {!embedded && (
        <PageHeader title="Followed Entities" sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => { setActiveTab(v); setPage(1); }} variant="scrollable"
            scrollButtons="auto" sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
            {entityTypes.map((type) => (
              <Tab key={type.key} label={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {type.label}
                  <Chip label={entityCounts[type.key] || 0} size="small" sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              )} />
            ))}
          </Tabs>
        </PageHeader>
      )}

      {embedded && <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setActiveTab(newValue);
          setPage(1);
        }}
        variant={compact ? "scrollable" : "scrollable"}
        scrollButtons={compact ? "auto" : "auto"}
        orientation={compact ? "vertical" : "horizontal"}
        sx={{ 
          mb: compact ? 1.5 : (embedded ? 2 : 3), 
          minHeight: compact ? 'auto' : 'auto',
          '& .MuiTabs-flexContainer': {
            flexDirection: compact ? 'column' : 'row',
            alignItems: compact ? 'stretch' : 'center'
          },
          '& .MuiTab-root': {
            minHeight: compact ? '32px' : 'auto',
            alignItems: compact ? 'flex-start' : 'center',
            textAlign: compact ? 'left' : 'center',
            px: compact ? 1 : 2
          }
        }}
      >
        {entityTypes.map((type, index) => (
          <Tab
            key={type.key}
            label={
              compact ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {type.icon}
                    <Typography variant="body2">
                      {type.label}
                    </Typography>
                  </Box>
                  <Chip
                    label={entityCounts[type.key] || 0}
                    size="small"
                    sx={{ height: 18, fontSize: '0.7rem', minWidth: 24 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {type.icon}
                  <span>{type.label}</span>
                  <Chip
                    label={entityCounts[type.key] || 0}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )
            }
          />
        ))}
      </Tabs>}

      {compact && (
        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ 
            mb: 1.5, 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              fontSize: '0.85rem',
              height: '32px',
            },
          }}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: compact ? 1.5 : 3 }}>
          {error}
        </Alert>
      )}

      {loading && page === 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 4 : 8 }}>
          <CircularProgress size={compact ? 24 : 40} />
        </Box>
      ) : filteredEntities.length === 0 && currentEntityType.key !== 'users' ? (
        <Alert severity="info" sx={{ fontSize: compact ? '0.8rem' : 'inherit' }}>
          {searchTerm ? 'No matching results found.' : `You are not following any ${currentEntityType.label.toLowerCase()} yet.`}
        </Alert>
      ) : (
        <>
          {compact ? (
            <Box>
              {filteredEntities.slice(0, maxItems || filteredEntities.length).map((entity) =>
                renderEntityCard(entity, currentEntityType.key)
              )}
              {maxItems && filteredEntities.length > maxItems && (
                <Box sx={{ textAlign: 'center', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button size="small" variant="text" onClick={() => navigate('/social/followed/')} sx={{ fontSize: '0.8rem' }}>
                    View All ({filteredEntities.length})
                  </Button>
                </Box>
              )}
            </Box>
          ) : currentEntityType.key === 'users' ? (
            /* Users tab: Following / Followers chip toggle */
            <Box>
              {/* Section toggle */}
              {/* on the page the title row already leaves room above; embedded, the toggle keeps its own */}
              <Stack direction="row" spacing={1} sx={{ mb: 3, mt: embedded ? 5 : 0 }}>
                {[
                  { key: 'following', label: 'Following', Icon: PersonIcon, count: filteredEntities.length },
                ].map(({ key, label, Icon, count }) => {
                  const active = userSection === key;
                  return (
                    <Box
                      key={key}
                      onClick={() => setUserSection(key)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.5, borderRadius: '16px', cursor: 'pointer',
                        border: '1px solid',
                        borderColor: active ? 'primary.main' : 'divider',
                        bgcolor: active ? 'primary.main' : 'transparent',
                        color: active ? 'primary.contrastText' : 'text.primary',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
                        userSelect: 'none',
                      }}
                    >

                      <Badge
                        badgeContent={count}
                        color="primary"
                        sx={{ '& .MuiBadge-badge': { 
                          fontSize: '0.8rem', height: 16, minWidth: 16, transform: 'translate(50%, -60%)', top: 0, left: 0, 
                          bgcolor: active ? 'white' : undefined, color: active ? 'primary.light' : undefined } }}
                      >
                        <Icon sx={{ fontSize: '1.35rem', color: active ? 'inherit' : 'primary.light' }} />
                      </Badge>
                      <Typography variant="body2" sx={{ fontWeight: active ? 700 : 400, ml:1 }}>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              {/* Following list */}
              {userSection === 'following' && (
                filteredEntities.length === 0 ? (
                  <Alert severity="info">You have no connections yet.</Alert>
                ) : (
                  <Grid container spacing={embedded ? 1.5 : 3}>
                    {filteredEntities.map((entity) => renderEntityCard(entity, 'users'))}
                  </Grid>
                )
              )}

              {/* Followers list */}
              {userSection === 'followers' && (
                followersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : followers.length === 0 ? (
                  <Alert severity="info">No connections yet.</Alert>
                ) : (
                  <Grid container spacing={embedded ? 1.5 : 3}>
                    {followers.map((entity) => renderEntityCard(entity, 'users'))}
                  </Grid>
                )
              )}
            </Box>
          ) : (
            <Grid container spacing={embedded ? 1.5 : 3}>
              {filteredEntities.map((entity) =>
                renderEntityCard(entity, currentEntityType.key)
              )}
            </Grid>
          )}
        </>
      )}
    </ContainerComponent>
  );
};

export default FollowedEntitiesPage;
